import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Alert, Box, Checkbox, Input } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import TextEllipsis from '../../../../../text/TextEllipsis';
import { handlerErrorV2 } from '../../../../../../utils/errors';
import CkeckboxRolesSkeleton from '../../../../../skeleton/CkeckboxRolesSkeleton';
import IconTooltipButton from '../../../../../button/IconTooltipButton';
import DialogConfirm from '../../../../../dialog/DialogConfirm';
import usePeriodicalReportSetting from '../../../../../../hooks/periodical-report/usePeriodicalReportSetting';

const DialogUpdateDescription = ({ open, onClose, form, onSubmit, isLoading }) => {
  const [definition, setDescription] = useState(form?.definition || '');
  const onSave = () => {
    onSubmit({
      ...form,
      definition
    });
  };
  return (
    <DialogConfirm
      id={`edit-definition-dialog`}
      open={open}
      title={'Level Description'}
      onCancel={onClose}
      onConfirm={onSave}
      textConfirm={'Save'}
      loading={isLoading}
      maxWidth={'xs'}
    >
      <Typography className='mb-2'>
        Definition for: <b>{form.name}</b>
      </Typography>
      <Input
        fullWidth
        multiline
        minRows={4}
        placeholder='Enter definition here...'
        value={definition}
        disabled={isLoading}
        onChange={(e) => setDescription(e.target.value)}
      />
    </DialogConfirm>
  );
};

const GroupLevel = ({
  form,
  onChangeLevel,
  type,
  items = [],
  groupName,
  groupId,
  onSelectedAll,
  onSubmitDescription
}) => {
  return (
    <>
      <div className='bg-loading px-4'>
        <FormControlLabel
          control={
            <Checkbox
              id={`checkbox-levels-${groupId}`}
              name={groupId}
              onChange={type === 'preview' ? undefined : (e) => onSelectedAll(groupId, e.target.checked)}
              color='primary'
              checked={
                items?.length ===
                form?.levelsId?.filter((item) => items?.map((i) => i._id).includes(item)).length
              }
            />
          }
          className={type === 'preview' ? 'pointer-events-none' : ''}
          label={<TextEllipsis id={`text-levels-${groupId}`}>{groupName}</TextEllipsis>}
        />
      </div>
      <div className='flex flex-col px-4'>
        {items?.map((item, index) => (
          <div key={item._id} className='flex items-center justify-between'>
            <FormControlLabel
              control={
                <Checkbox
                  id={`checkbox-levels-${groupId}-${index}-${item._id}`}
                  checked={form?.levelsId?.includes(item._id) ? true : false}
                  name={item._id}
                  onChange={type === 'preview' ? undefined : (e) => onChangeLevel(item._id, e.target.checked)}
                  color='primary'
                />
              }
              className={type === 'preview' ? 'pointer-events-none' : ''}
              label={<TextEllipsis id={`text-levels-${index}-${item._id}`}>{item.name}</TextEllipsis>}
            />
            {form?.levelsId?.includes(item._id) && (
              <IconTooltipButton
                icon='comment-dots'
                title={item.definition || 'No definition'}
                size='small'
                color='primary'
                className={item.definition ? '' : 'opacity-25'}
                onClick={(e) => onSubmitDescription(item)}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

const FormLevels = ({ form, onChange, type, curriculumId, curriculumName, curriculumList, errors }) => {
  const { updateLevelAssessmentArea } = usePeriodicalReportSetting();

  const [modal, setModal] = useState(false);
  const [list, setList] = useState({});
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [error, setError] = useState(null);

  const onLoad = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const curriculum = curriculumList?.find((item) => item._id === curriculumId);
      if (curriculum) {
        const groupBy = 'curriculumId';
        const levels = curriculum?.levels.map((item) => ({
          ...item,
          curriculumId
        }));
        const listData = groupBy
          ? levels.reduce((acc, item) => {
              const groupNameBy = item[groupBy] || 'General';
              if (!acc[groupNameBy]) acc[groupNameBy] = [];
              let definition = '';
              const find = form.levels?.find((level) => level.levelId === item._id);
              if (find) {
                definition = find.definition || '';
              }
              acc[groupNameBy].push({
                ...item,
                definition,
                curriculumId
              });
              return acc;
            }, {})
          : { 'General': levels };

        let datalist = {};

        if (listData.General) {
          datalist.General = listData.General;
        }
        if (listData[curriculumId]) {
          datalist[curriculumId] = listData[curriculumId];
        }
        setList(datalist);
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError(message);
    } finally {
      setIsLoadingList(false);
    }
  }, [curriculumId, form.levels]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const onChangeLevel = (id, checked) => {
    const newLevels = new Set(form.levelsId);
    setError(null);
    if (checked) {
      newLevels.add(id);
    } else {
      newLevels.delete(id);
    }
    onChange('levelsId', [...newLevels]);
  };

  const onSelectedAll = (curriculumId, checked) => {
    const listdata = list?.[curriculumId] || [];
    const ids = listdata?.map((item) => String(item._id)) || [];
    const oldLevels = form.levelsId?.filter((item) => !ids?.includes(String(item))) || [];
    if (checked) {
      onChange('levelsId', [...oldLevels, ...ids]);
    } else {
      onChange('levelsId', oldLevels);
    }
  };

  const generalData = list?.['General'];

  useEffect(() => {
    if (errors?.levels) {
      setError(errors?.levels);
    }
  }, [errors?.levels]);

  const onSubmitDescription = async (value) => {
    const newLevels =
      form.levels?.map((level) => {
        if (level.levelId === value._id) {
          return {
            ...level,
            definition: value.definition
          };
        }
        return level;
      }) || [];
    const isExist = form.levels?.find((level) => level.levelId === value._id);
    if (!isExist) {
      newLevels.push({
        levelId: value._id,
        definition: value.definition
      });
    }
    onChange('levels', newLevels);
    if (form._id) {
      setIsLoadingSave(true);
      let result = await updateLevelAssessmentArea(form._id, value._id, {
        definition: value.definition
      });
      if (result.error) {
        setError(result.message || 'Error updating level definition');
      } else {
        setModal(false);
      }
      setIsLoadingSave(false);
    } else {
      setModal(false);
    }
  };

  return (
    <div className='border-l border-muted'>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          Levels
        </Typography>
      </Box>
      <Box className='px-2 py-2 h-screen-half overflow-auto'>
        {error && <Alert severity='error'>{error}</Alert>}
        <>
          {isLoadingList ? (
            <CkeckboxRolesSkeleton />
          ) : (
            <>
              {generalData?.length > 0 && (
                <GroupLevel
                  form={form}
                  onChangeLevel={onChangeLevel}
                  type={type}
                  items={generalData?.sort((a, b) => a.position - b.position)}
                  groupName='General'
                  groupId='General'
                  onSelectedAll={onSelectedAll}
                  onSubmitDescription={(value) => setModal(value)}
                />
              )}
              {Object.keys(list)
                ?.filter((key) => key !== 'General')
                ?.map((key) => {
                  return (
                    <GroupLevel
                      form={form}
                      onChangeLevel={onChangeLevel}
                      type={type}
                      items={list[key].sort((a, b) => a.position - b.position) || []}
                      groupName={curriculumName}
                      groupId={key}
                      onSelectedAll={onSelectedAll}
                      onSubmitDescription={(value) => setModal(value)}
                    />
                  );
                })}
            </>
          )}
        </>
      </Box>
      {modal && (
        <DialogUpdateDescription
          open={!!modal}
          onClose={() => setModal(false)}
          form={modal}
          isLoading={isLoadingSave}
          onSubmit={onSubmitDescription}
        />
      )}
    </div>
  );
};

export default FormLevels;
